'use client';

/** SSOT mapping semantic icon names to SDL-OWNED assets (no third-party icon dependencies). Naming is intent-based, never appearance-based. */

import { TrashIcon } from './assets/TrashIcon.tsx';
import { XIcon } from './assets/XIcon.tsx';
import { SearchIcon } from './assets/SearchIcon.tsx';
import { CheckIcon } from './assets/CheckIcon.tsx';
import { PlusIcon } from './assets/PlusIcon.tsx';
import { MinusIcon } from './assets/MinusIcon.tsx';
import { PencilIcon } from './assets/PencilIcon.tsx';
import { CopyIcon } from './assets/CopyIcon.tsx';
import { UploadIcon } from './assets/UploadIcon.tsx';
import { DownloadIcon } from './assets/DownloadIcon.tsx';
import { RefreshIcon } from './assets/RefreshIcon.tsx';
import { SendIcon } from './assets/SendIcon.tsx';
import { ShareIcon } from './assets/ShareIcon.tsx';
import { LinkIcon } from './assets/LinkIcon.tsx';
import { BookmarkIcon, BookmarkIconFilled } from './assets/BookmarkIcon.tsx';
import { ArrowLeftIcon } from './assets/ArrowLeftIcon.tsx';
import { ArrowRightIcon } from './assets/ArrowRightIcon.tsx';
import { ChevronDownIcon } from './assets/ChevronDownIcon.tsx';
import { ChevronUpIcon } from './assets/ChevronUpIcon.tsx';
import { PlayIcon } from './assets/PlayIcon.tsx';
import { ChevronLeftIcon } from './assets/ChevronLeftIcon.tsx';
import { ChevronRightIcon } from './assets/ChevronRightIcon.tsx';
import { MenuIcon } from './assets/MenuIcon.tsx';
import { MoreVerticalIcon } from './assets/MoreVerticalIcon.tsx';
import { HomeIcon } from './assets/HomeIcon.tsx';
import { SplitViewIcon } from './assets/SplitViewIcon.tsx';
import { InfoIcon } from './assets/InfoIcon.tsx';
import { AlertCircleIcon } from './assets/AlertCircleIcon.tsx';
import { BellIcon } from './assets/BellIcon.tsx';
import { WarningIcon } from './assets/WarningIcon.tsx';
import { LoaderIcon } from './assets/LoaderIcon.tsx';
import { SpinnerIcon } from './assets/SpinnerIcon.tsx';
import { StarIcon } from './assets/StarIcon.tsx';
import { CheckCircleIcon } from './assets/CheckCircleIcon.tsx';
import { XCircleIcon } from './assets/XCircleIcon.tsx';
import { UsersIcon } from './assets/UsersIcon.tsx';
import { UserIcon } from './assets/UserIcon.tsx';
import { UserCircleIcon } from './assets/UserCircleIcon.tsx';
import { TableIcon } from './assets/TableIcon.tsx';
import { LockIcon } from './assets/LockIcon.tsx';
import { EyeIcon } from './assets/EyeIcon.tsx';
import { SettingsIcon } from './assets/SettingsIcon.tsx';
import { CalendarIcon } from './assets/CalendarIcon.tsx';
import { FilterIcon } from './assets/FilterIcon.tsx';
import { ClockIcon } from './assets/ClockIcon.tsx';
import { MapIcon } from './assets/MapIcon.tsx';
import { CompassIcon } from './assets/CompassIcon.tsx';
import { BoltIcon } from './assets/BoltIcon.tsx';
import { SparklesIcon } from './assets/SparklesIcon.tsx';
import { PauseIcon } from './assets/PauseIcon.tsx';
import { ImageIcon } from './assets/ImageIcon.tsx';
import { FileIcon } from './assets/FileIcon.tsx';
import { DocumentTextIcon } from './assets/DocumentTextIcon.tsx';
import { InboxIcon } from './assets/InboxIcon.tsx';
import { PhoneIcon } from './assets/PhoneIcon.tsx';
import { ChatIcon } from './assets/ChatIcon.tsx';
import { ChartIcon } from './assets/ChartIcon.tsx';
import { ChartBarIcon } from './assets/ChartBarIcon.tsx';
import { TrendingUpIcon } from './assets/TrendingUpIcon.tsx';
import { ListIcon } from './assets/ListIcon.tsx';
import { GridIcon } from './assets/GridIcon.tsx';
import { RectangleStackIcon } from './assets/RectangleStackIcon.tsx';
import { CircleStackIcon } from './assets/CircleStackIcon.tsx';
import { BedIcon } from './assets/BedIcon.tsx';
import { BathIcon } from './assets/BathIcon.tsx';
import { RulerIcon } from './assets/RulerIcon.tsx';
import { StairsIcon } from './assets/StairsIcon.tsx';
import { GarageIcon } from './assets/GarageIcon.tsx';
import { PaletteIcon } from './assets/PaletteIcon.tsx';
import { FlameIcon } from './assets/FlameIcon.tsx';
import { SnowflakeIcon } from './assets/SnowflakeIcon.tsx';
import { WaveIcon } from './assets/WaveIcon.tsx';
import { DropletIcon } from './assets/DropletIcon.tsx';
import { DrainIcon } from './assets/DrainIcon.tsx';
import { PlugIcon } from './assets/PlugIcon.tsx';
import { ArrowDownIcon } from './assets/ArrowDownIcon.tsx';
import { ArrowUpIcon } from './assets/ArrowUpIcon.tsx';
import { TreeIcon } from './assets/TreeIcon.tsx';
import { MountainIcon } from './assets/MountainIcon.tsx';
import { DollarIcon } from './assets/DollarIcon.tsx';
import { GraduationCapIcon } from './assets/GraduationCapIcon.tsx';
import type { IconProps, SemanticIconName } from '@federa/types/ui-icons';

/** SemanticIcon is the preferred entry point for intent-based icon use; direct asset import via `@federa/components/icons/assets/*` is sanctioned only for one-off / non-semantic uses. */
const iconRegistry: Record<SemanticIconName, React.ComponentType<IconProps>> = {
	submit: SendIcon,
	check: CheckIcon,
	add: PlusIcon,
	edit: PencilIcon,
	cancel: XIcon,
	close: XIcon,
	back: ArrowLeftIcon,
	'arrow-back': ArrowLeftIcon,
	forward: ArrowRightIcon,
	delete: TrashIcon,
	remove: MinusIcon,
	info: InfoIcon,
	warning: WarningIcon,
	alert: AlertCircleIcon,
	bell: BellIcon,
	share: ShareIcon,
	download: DownloadIcon,
	upload: UploadIcon,
	copy: CopyIcon,
	link: LinkIcon,
	next: ChevronRightIcon,
	previous: ChevronLeftIcon,
	expand: ChevronDownIcon,
	'chevron-up': ChevronUpIcon,
	collapse: SplitViewIcon,
	search: SearchIcon,
	filter: FilterIcon,
	refresh: RefreshIcon,
	moreVertical: MoreVerticalIcon,
	menu: MenuIcon,
	settings: SettingsIcon,
	bookmark: BookmarkIcon,
	'bookmark-solid': BookmarkIconFilled,
	map: MapIcon,
	compass: CompassIcon,
	calendar: CalendarIcon,
	clock: ClockIcon,
	home: HomeIcon,
	pause: PauseIcon,
	play: PlayIcon,
	image: ImageIcon,
	file: FileIcon,
	document: DocumentTextIcon,
	success: CheckCircleIcon,
	error: XCircleIcon,
	loading: LoaderIcon,
	spinner: SpinnerIcon,
	pending: ClockIcon,
	view: EyeIcon,
	locked: LockIcon,
	phone: PhoneIcon,
	chat: ChatIcon,
	send: SendIcon,
	chart: ChartIcon,
	analytics: ChartBarIcon,
	trending: TrendingUpIcon,
	list: ListIcon,
	grid: GridIcon,
	bolt: BoltIcon,
	sparkles: SparklesIcon,
	star: StarIcon,
	inbox: InboxIcon,
	network: CircleStackIcon,
	users: UsersIcon,
	user: UserIcon,
	// THE SIGNED-IN PERSON'S ACCOUNT, not a financial one (iss-b515ab4b): minted beside a card glyph lifted from the
	// finance accounts page, it drew a payment card on the profile app's account button. A payment card has no semantic
	// name -- nothing on the roster means one -- and the finance pages import that asset directly.
	account: UserCircleIcon,
	ledger: TableIcon,
	folder: RectangleStackIcon,

	// Property facts (see the SemanticIconName note). `home`, `calendar`, `users` and `info` are DELIBERATELY absent
	// from this block: the property-facts set duplicated those four glyphs, so those rows resolve to the assets
	// already registered above rather than to a fifth, sixth, seventh and eighth copy.
	bedroom: BedIcon,
	bathroom: BathIcon,
	measurement: RulerIcon,
	stories: StairsIcon,
	garage: GarageIcon,
	'architectural-style': PaletteIcon,
	heating: FlameIcon,
	cooling: SnowflakeIcon,
	'water-feature': WaveIcon,
	'water-supply': DropletIcon,
	sewer: DrainIcon,
	electric: PlugIcon,
	'below-grade': ArrowDownIcon,
	'above-grade': ArrowUpIcon,
	lot: TreeIcon,
	topography: MountainIcon,
	fee: DollarIcon,
	school: GraduationCapIcon,
};

/** Total over `SemanticIconName` -- a plain lookup that never throws. */
export function getIcon(name: SemanticIconName): React.ComponentType<IconProps> {
	return iconRegistry[name];
}

/**
 * Boundary guard narrowing an OPEN icon-name string onto the closed {@link SemanticIconName} vocabulary. Every surface
 * that holds a name as bare `string` -- `ButtonIconName`, `SidebarNavItem.icon`, `MetricCardData.icon` -- narrows HERE
 * instead of asserting `name as SemanticIconName`, because the assertion silences the compiler without checking
 * anything: an unrecognized name then reaches `getIcon`, resolves to `undefined`, and React throws on the element type
 * at render. Rejecting at the boundary costs the caller one icon and nothing else.
 *
 * Keyed on `iconRegistry`'s OWN keys rather than a second roster: the record is typed `Record<SemanticIconName, ...>`,
 * so TypeScript already forces its key set to equal the union and this predicate cannot drift from it. `Object.hasOwn`
 * rather than `in` is load-bearing -- `in` answers true for inherited `Object.prototype` members, so `'constructor'`
 * and `'toString'` would pass as icon names.
 */
export function isSemanticIconName(name: string): name is SemanticIconName {
	return Object.hasOwn(iconRegistry, name);
}
